import { CourseCard } from '@/components/ui/custom/coursePath/card/coursePathCard/CoursePathCard';
import Tag from '@/components/ui/custom/dataDisplay/Tag';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useAuth from '@/data/hook/useAuth';
import useListService from '@/data/hook/useListService';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import handleButtonContent, {
  isPulished,
  isVersioning,
} from '@/functions/handleCourseStatusIcon';
import CoursePath from '@/models/CoursePath';
import { courseHasVersions, getBannerImg } from '@/utils/coursePath';
import { useMemo } from 'react';
import CoursePathItemFooter from './CoursePathItemFooter';

type CommonProps = {
  className?: string;
};

type CourseCardProps = {
  isLoading?: boolean;
  coursePath: CoursePath;
  handleSelectCourse: (course: CoursePath) => void;
};

type LoadingCardProps = {
  isLoading: true;
  coursePath?: undefined;
  handleSelectCourse?: undefined;
};

type CoursePathItemProps = CommonProps & (CourseCardProps | LoadingCardProps);

export default function CoursePathItem({
  isLoading,
  className,
  coursePath,
  handleSelectCourse,
}: CoursePathItemProps) {
  const { user } = useAuth();

  const course = coursePath?.course;
  const { results: coursePaths, isInitialLoading: isLoadingCourses } =
    useListService({
      ...coursePathsQueryKeys.list({
        courseId: course ? [course.id] : undefined,
        ordering: '-version',
      }),
      enabled: courseHasVersions(
        coursePath?.version || 1,
        course?.lastCoursePathVersion,
      ),
      staleTime: REQUEST_STALE_TIME_IN_MS,
    });

  const versioningTags = useMemo(() => {
    if (!coursePath) return [];
    if (coursePath?.version <= 1 && isVersioning(coursePath.status)) {
      return [
        handleButtonContent(
          coursePath.status,
          coursePath.version,
          'w-3.5 h-3.5',
        ),
      ];
    }

    const getMainNonPublishedCourses = () => {
      const courses: CoursePath[] = [];
      const versioningStatus = [
        VersioningStatusEnum.EDITING,
        VersioningStatusEnum.VERSIONING,
        VersioningStatusEnum.BETA,
      ];
      for (const coursePath of coursePaths) {
        if (courses.length >= versioningStatus.length) break;
        if (!versioningStatus.includes(coursePath.status)) continue;
        if (courses.some(({ status }) => status === coursePath.status))
          continue;
        courses.push(coursePath);
      }
      return courses;
    };

    const nonPublishedCourses = getMainNonPublishedCourses();
    return nonPublishedCourses.map(({ status, version }) =>
      handleButtonContent(status, version, 'w-3.5 h-3.5'),
    );
  }, [coursePath, coursePaths]);

  const recentPublishedCourse =
    coursePaths.find(({ status }) => isPulished(status)) || coursePath;

  if (isLoading) {
    return <CourseCard loading className={className} />;
  }

  return (
    <CourseCard className={className} to={`/courses/${coursePath.slug}`}>
      <CourseCard.Header className={{ base: 'relative' }}>
        <CourseCard.Image src={getBannerImg(coursePath)} />
        <div className="absolute top-1.5 right-2 flex gap-1 flex-wrap justify-end">
          {versioningTags.map(({ text, icon }) => (
            <Tag
              key={text}
              text={text}
              icon={icon}
              color="custom"
              className={{
                base: 'bg-secondary-content rounded-md gap-1 !text-secondary px-1.5 py-1 font-400 text-12 sm:text-14',
              }}
            />
          ))}
          <CourseCard.Rating rating={recentPublishedCourse?.rating} />
        </div>
      </CourseCard.Header>

      <CourseCard.Body>
        <CourseCard.Header>
          <div className="relative flex gap-y-1 gap-x-2.5 flex-wrap items-center truncate">
            <CourseCard.Title
              text={coursePath.course.name}
              className="text-16"
            />
            <CourseCard.Formats formats={coursePath.course.formats} />
          </div>

          <CourseCard.Version version={recentPublishedCourse?.version} />
        </CourseCard.Header>
      </CourseCard.Body>

      <CourseCard.Actions
        coursePath={coursePath}
        coursePathCollection={coursePaths}
        loading={isLoadingCourses}
        handleSelectCoursePath={handleSelectCourse}
        user={user}
      />

      <CourseCard.Footer>
        <CoursePathItemFooter
          coursePath={coursePath}
          coursePathList={coursePaths}
        />
      </CourseCard.Footer>
    </CourseCard>
  );
}
